import { Phone, Mail, MapPin, Clock, Siren } from "lucide-react";
import SEO from "../components/SEO";
import QuoteForm from "../components/QuoteForm";
import { SITE } from "../lib/site";

export default function Contact() {
  return (
    <>
      <SEO
        title="Contact & Quote — Alliance Crane Service"
        description="Request a quote or call our DeLand dispatch. Central Florida mobile crane rental, 24/7 emergency lifting."
      />

      <section className="border-b border-[var(--ac-border)] bg-black">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-14 md:py-24">
          <div className="overline mb-4">Contact</div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl max-w-4xl leading-[0.95]">
            Let's plan the lift.
          </h1>
          <p className="mt-5 text-zinc-400 max-w-2xl text-lg">
            Tell us about your job and we'll get back fast with availability and pricing.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <QuoteForm />
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="surface p-7">
              <div className="overline mb-5">Direct Line</div>
              <div className="space-y-5">
                <a
                  href={`tel:${SITE.phoneE164}`}
                  className="flex items-start gap-3 group"
                  data-testid="contact-phone"
                >
                  <div className="w-10 h-10 border border-[var(--ac-border)] flex items-center justify-center bg-black">
                    <Phone className="w-4 h-4 text-[var(--ac-red)]" />
                  </div>
                  <div>
                    <div className="overline !text-[10px]">Phone</div>
                    <div className="font-display text-2xl group-hover:text-[var(--ac-red)] transition-colors">
                      {SITE.phone}
                    </div>
                  </div>
                </a>

                <a
                  href={`mailto:${SITE.email}`}
                  className="flex items-start gap-3 group"
                  data-testid="contact-email"
                >
                  <div className="w-10 h-10 border border-[var(--ac-border)] flex items-center justify-center bg-black">
                    <Mail className="w-4 h-4 text-[var(--ac-red)]" />
                  </div>
                  <div>
                    <div className="overline !text-[10px]">Email</div>
                    <div className="text-base group-hover:text-[var(--ac-red)] transition-colors break-all">
                      {SITE.email}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 border border-[var(--ac-border)] flex items-center justify-center bg-black">
                    <MapPin className="w-4 h-4 text-[var(--ac-red)]" />
                  </div>
                  <div>
                    <div className="overline !text-[10px]">Headquarters</div>
                    <div>{SITE.address}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="surface p-7">
              <div className="overline mb-5">Hours</div>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between border-b border-[var(--ac-border)] pb-2">
                  <span className="flex items-center gap-2 text-zinc-300">
                    <Clock className="w-4 h-4 text-[var(--ac-red)]" /> Mon – Fri
                  </span>
                  <span className="font-mono-data">7:00 AM – 5:00 PM</span>
                </div>
                <div className="flex items-center justify-between border-b border-[var(--ac-border)] pb-2">
                  <span className="text-zinc-300 pl-6">Saturday</span>
                  <span className="font-mono-data">By Appointment</span>
                </div>
                <div className="flex items-center justify-between text-[var(--ac-red)] font-semibold">
                  <span className="flex items-center gap-2">
                    <Siren className="w-4 h-4" /> Emergency
                  </span>
                  <span className="font-mono-data">24 / 7</span>
                </div>
              </div>
              <a
                href={`tel:${SITE.phoneE164}`}
                className="btn-primary w-full justify-center mt-6"
                data-testid="contact-emergency-call"
              >
                <Siren className="w-4 h-4" /> Emergency Dispatch
              </a>
            </div>

            <div className="surface p-7">
              <div className="overline mb-3">Worked With Us?</div>
              <div className="flex items-center gap-1 text-[var(--ac-yellow)] mb-3" aria-hidden="true">
                {[0,1,2,3,4].map((i) => (
                  <svg key={i} className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6L12 2z"/>
                  </svg>
                ))}
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                Your Google review helps other Central Florida contractors find us.
              </p>
              <a
                href={SITE.googleReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-google-review"
                className="btn-primary w-full justify-center"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.6 7-6.2-3.8-6.2 3.8 1.6-7L2 9.5l7.1-.6L12 2z"/>
                </svg>
                Leave a Google Review
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
